import StarterKit from "@tiptap/starter-kit";
import { Markdown } from "tiptap-markdown";
import Mention from "@tiptap/extension-mention";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import Image from "@tiptap/extension-image";
import FileHandler from "@tiptap/extension-file-handler";
import Placeholder from "@tiptap/extension-placeholder";
import type { Editor, Extensions } from "@tiptap/core";
import type { SuggestionOptions } from "@tiptap/suggestion";
import { mentionSuggestionOptions } from "../mentionSuggestion";
import { Video } from "../extensions/Video";
import { PreserveEmptyLines } from "../extensions/PreserveEmptyLines";
import { HorizontalRuleMarkdown } from "../extensions/HorizontalRuleMarkdown";
import { HeadingWithAnchors } from "../extensions/HeadingWithAnchors";
import type MarkdownIt from "markdown-it";
import { addMentionMarkdownPlugin } from "../utils/markdownPlugin";

interface UseEditorExtensionsProps {
  enableMentions?: boolean;
  mentionOptions?: Omit<SuggestionOptions, "editor">;
  placeholder?: string;
  onFileDrop?: (
    editor: Editor,
    files: File[],
    pos: number
  ) => void | Promise<void>;
  onFilePaste?: (editor: Editor, files: File[]) => void | Promise<void>;
}

/**
 * Hook to configure TipTap editor extensions
 * Returns memoized array of extensions based on configuration
 */
export const useEditorExtensions = ({
  enableMentions = false,
  mentionOptions,
  placeholder,
  onFileDrop,
  onFilePaste,
}: UseEditorExtensionsProps) => {
  const baseExtensions: Extensions = [
    StarterKit.configure({
      heading: false, // Disable default heading, we'll use our custom one
      horizontalRule: {
        HTMLAttributes: {
          class: "editor-hr",
        },
      },
      link: {
        openOnClick: false,
        HTMLAttributes: {
          class: "editor-link",
        },
      },
    }),
    HeadingWithAnchors.configure({
      levels: [1, 2, 3],
    }),
    Markdown.configure({
      html: true,
      transformPastedText: true,
      transformCopiedText: true,
      breaks: true, // Convert line breaks to <br> tags
    }),
    Placeholder.configure({
      placeholder:
        placeholder ||
        "Write your snippet here. Highlight text to format, link, or style it",
      emptyEditorClass: "is-editor-empty",
      showOnlyWhenEditable: true,
    }),
    TaskList,
    TaskItem.configure({
      nested: true,
    }),
    Image.configure({
      inline: false,
      allowBase64: false,
      HTMLAttributes: {
        class: "editor-image",
        style: "max-width: 100%; height: auto;",
      },
    }),
    Video,
    PreserveEmptyLines,
    HorizontalRuleMarkdown,
    FileHandler.configure({
      allowedMimeTypes: [
        "image/png",
        "image/jpeg",
        "image/webp",
        "video/mp4",
        "video/webm",
      ],
      onDrop: (currentEditor, files, pos) => {
        if (onFileDrop) {
          onFileDrop(currentEditor, files, pos);
        }
      },
      onPaste: (currentEditor, files) => {
        if (onFilePaste) {
          onFilePaste(currentEditor, files);
        }
      },
    }),
  ];

  if (enableMentions) {
    baseExtensions.push(
      Mention.extend({
        addStorage() {
          return {
            markdown: {
              serialize(
                state: { text: (text: string, escape?: boolean) => void },
                node: { attrs: { label: string; id: string } }
              ) {
                const attrs = node.attrs;
                const label = attrs.label ?? attrs.id;
                state.text(`@[${label}](${attrs.id})`, false);
              },
              parse: {
                setup(markdownit: MarkdownIt) {
                  addMentionMarkdownPlugin(markdownit);
                  markdownit.set({ breaks: true });
                },
              },
            },
          };
        },
      }).configure({
        HTMLAttributes: {
          class: "mention",
        },
        renderLabel({ node }) {
          return `@${node.attrs.label ?? node.attrs.id}`;
        },
        suggestion: mentionOptions || mentionSuggestionOptions,
      })
    );
  }

  return baseExtensions;
};
